import React, { useEffect } from "react";
import { Text } from "@mantine/core";
import { useParams } from "react-router-dom";
import {
  isFullV2Definition,
  isLedgerDefinition,
} from "../../../types/reportsV2";
import { setLastViewedReportId } from "../../../utils/reportsLastViewed";
import MobileReportHeader, {
  MobileBackLinkBar,
} from "../mobile/MobileReportHeader";
import ReportCanvas, { CanvasHeaderProps } from "../v2/ReportCanvas";
import SummaryCanvas from "../v2/summary/SummaryCanvas";
import LegacyReportBody from "./LegacyReportBody";
import ReportHeader from "./ReportHeader";
import { reportDisplayDescription, reportDisplayName } from "./reportCatalog";
import { useReportsShell } from "./reportsShellContext";
import { sharedNoteFor } from "./reportSharing";
import styles from "./ReportsShell.module.css";

/**
 * /admin/reports/:reportId — the selected report: ReportHeader (name,
 * description, "…" menu) over its body. Legacy report types render the
 * untouched legacy ReportView; new-engine types render the v2 ReportCanvas
 * (or, for a summary such as the Sales Summary, the ledger SummaryCanvas),
 * which owns the header's Save view / Discard actions. On a phone the header
 * is MobileReportHeader (back link to the list, "…" sheet). Only the header
 * changes with the layout — the body keeps its place, and the v2 canvas
 * keeps its draft — so crossing the mobile breakpoint never remounts it.
 */
export default function ReportInstancePage() {
  const { reportId } = useParams<{ reportId: string }>();
  const shell = useReportsShell();
  const { userId, salonId } = shell;
  const report = shell.reports.find((r) => r.id === reportId) ?? null;
  const foundId = report?.id;

  useEffect(() => {
    if (foundId) setLastViewedReportId(userId, salonId, foundId);
  }, [foundId, userId, salonId]);

  if (shell.loading || shell.loadError) return null;
  if (!report) {
    const missing = (
      <Text className={styles.message}>
        That report doesn&apos;t exist or has been removed.
      </Text>
    );
    return (
      <>
        {shell.isMobile && <MobileBackLinkBar />}
        {missing}
      </>
    );
  }

  const entry = shell.catalog.byType.get(report.report_type);
  const header: CanvasHeaderProps = {
    name: reportDisplayName(report, shell.catalog),
    description: reportDisplayDescription(report, shell.catalog),
    defaultName: entry?.name ?? "",
    autoEdit: shell.editingReportId === report.id,
    onAutoEditHandled: shell.clearEditingReport,
    onSaveMeta: (name, description) =>
      shell.saveReportMeta(report, name, description),
    onRemove: () => shell.requestRemove(report),
    onShare: shell.requestShare
      ? () => shell.requestShare?.(report)
      : undefined,
    sharedNote: sharedNoteFor(report),
  };

  if (entry?.engine === "v2" && isFullV2Definition(entry.v2Definition)) {
    const Canvas = isLedgerDefinition(entry.v2Definition)
      ? SummaryCanvas
      : ReportCanvas;
    return (
      <Canvas
        key={report.id}
        mobile={shell.isMobile}
        salonId={salonId}
        report={report}
        definition={entry.v2Definition}
        header={header}
        onReportSaved={shell.updateReportInList}
        onDuplicate={() => shell.duplicateReport(report)}
        onDirtyChange={(dirty) => shell.setReportDirty(report.id, dirty)}
      />
    );
  }

  let body: React.ReactNode;
  if (entry?.engine === "legacy" && entry.legacyDefinition) {
    body = (
      <LegacyReportBody
        salonId={salonId}
        definition={entry.legacyDefinition}
        report={report}
        isDeleting={shell.removingReportId === report.id}
        onReportUpdated={shell.updateReportInList}
        onDelete={() => shell.requestRemove(report)}
      />
    );
  } else if (entry?.engine === "v2") {
    body = (
      <Text className={styles.message}>
        This report can&apos;t be shown right now. Refresh the page to try
        again.
      </Text>
    );
  } else {
    body = (
      <Text className={styles.message}>
        This report&apos;s definition is no longer available. Remove it from
        your reports list.
      </Text>
    );
  }

  const duplicate = () => shell.duplicateReport(report);
  return (
    <>
      {shell.isMobile ? (
        <MobileReportHeader
          key={report.id}
          header={header}
          onDuplicate={duplicate}
        />
      ) : (
        <ReportHeader key={report.id} {...header} onDuplicate={duplicate} />
      )}
      {body}
    </>
  );
}
